import PropTypes from 'prop-types';
import React from 'react';
import { View, StyleSheet, Text } from 'react-native-web';
import { Checkbox } from '@fluentui/react';

import Avatar from './Avatar';
import Bubble from './Bubble';
import SystemMessage from './SystemMessage';
import Day from './Day';

import { isSameUser, isSameDay } from './utils';

const styles = {
  left: StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      marginLeft: 8,
      marginRight: 0
    },
    bubbleContainer: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      flex: 1,
      minWidth: 0,
      maxWidth: '100%'
    },
    username: {
      fontSize: 12,
      color: 'var(--idigest-chat-fg, #000000)',
      marginBottom: 6,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      padding: 0
    }
  }),
  right: StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-end',
      marginLeft: 0,
      marginRight: 8
    }
  })
};

export default class Message extends React.Component {
  shouldComponentUpdate(nextProps) {
    const next = nextProps.currentMessage;
    const current = this.props.currentMessage;
    const isSelectedChanged =
      nextProps.isBatchSelectMode &&
      nextProps.selectedMessageIds &&
      this.props.selectedMessageIds &&
      nextProps.selectedMessageIds.has(next.id) !== this.props.selectedMessageIds.has(next.id);
    return (
      next.send !== current.send ||
      next.received !== current.received ||
      next.pending !== current.pending ||
      next.createdAt !== current.createdAt ||
      next.text !== current.text ||
      next.image !== current.image ||
      next.video !== current.video ||
      nextProps.isBatchSelectMode !== this.props.isBatchSelectMode ||
      isSelectedChanged ||
      nextProps.messageActionSheet !== this.props.messageActionSheet ||
      nextProps.playingAudioId !== this.props.playingAudioId
    );
  }

  getInnerComponentProps = () => {
    //eslint-disable-next-line
    const { containerStyle, ...props } = this.props;
    return {
      ...props,
      isSameUser,
      isSameDay
    };
  };

  renderDay() {
    if (this.props.currentMessage.createdAt) {
      const dayProps = this.getInnerComponentProps();
      if (this.props.renderDay) {
        return this.props.renderDay(dayProps);
      }
      return <Day {...dayProps} />;
    }
    return null;
  }

  renderBubble() {
    const bubbleProps = this.getInnerComponentProps();
    if (this.props.renderBubble) {
      const customBubble = this.props.renderBubble(bubbleProps);
      if (customBubble !== null && customBubble !== undefined) {
        return customBubble;
      }
    }
    return <Bubble {...bubbleProps} />;
  }

  renderSystemMessage() {
    const systemMessageProps = this.getInnerComponentProps();
    if (this.props.renderSystemMessage) {
      return this.props.renderSystemMessage(systemMessageProps);
    }
    return <SystemMessage {...systemMessageProps} />;
  }

  renderAvatar() {
    if (this.props.user.id === this.props.currentMessage.user.id && !this.props.showUserAvatar) {
      return null;
    }
    const avatarProps = this.getInnerComponentProps();
    const { currentMessage } = avatarProps;
    if (currentMessage.user.avatar === null) {
      return null;
    }
    if (this.props.position === 'left') {
      const isSameUserAsPrevious = isSameUser(this.props.currentMessage, this.props.previousMessage);
      if (!isSameUserAsPrevious) {
        return <Avatar {...avatarProps} showAvatarForEveryMessage={true} renderAvatarOnTop={true} />;
      }
      return <Avatar {...avatarProps} showAvatarForEveryMessage={false} renderAvatarOnTop={true} />;
    }
    return <Avatar {...avatarProps} />;
  }

  renderUsername() {
    if (this.props.position !== 'left') {
      return null;
    }
    const { currentMessage } = this.props;
    if (!currentMessage || !currentMessage.user || !currentMessage.user.name) {
      return null;
    }
    if (isSameUser(this.props.currentMessage, this.props.previousMessage)) {
      return null;
    }
    return <Text style={styles.left.username}>{currentMessage.user.name}</Text>;
  }

  renderCheckbox() {
    const { isBatchSelectMode, selectedMessageIds, toggleMessageSelection, currentMessage, position } = this.props;
    if (!isBatchSelectMode || !currentMessage || !currentMessage.id) {
      return null;
    }
    const isSelected = selectedMessageIds && selectedMessageIds.has(currentMessage.id);

    const checkboxRight = position === 'right' ? -4 : 4;
    const hasUsername = position === 'left' && currentMessage?.user?.name;
    const checkboxTop = position === 'right' ? '18px' : hasUsername ? '14px' : '0px';

    const checkboxContainerStyle = {
      position: 'absolute',
      right: `${checkboxRight}px`,
      top: checkboxTop,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-end',
      zIndex: 10,
      pointerEvents: 'auto',
      backgroundColor: 'var(--idigest-chat-surface, #f5f5f5)'
    };

    return React.createElement(
      'div',
      {
        style: checkboxContainerStyle,
        onClick: (e) => {
          e.stopPropagation();
        }
      },
      React.createElement(Checkbox, {
        checked: isSelected || false,
        onChange: (ev, checked) => {
          if (ev) {
            ev.stopPropagation();
          }
          if (checked !== undefined && toggleMessageSelection) {
            toggleMessageSelection(currentMessage.id);
          }
        },
        styles: {
          checkbox: {
            width: '17px',
            height: '17px',
            backgroundColor: 'var(--idigest-chat-surface, #f5f5f5)',
            borderColor: 'var(--idigest-chat-muted, #b0b0b0)',
            selectors: {
              '&:hover': {
                backgroundColor: 'var(--idigest-chat-surface-hover, #f5f5f5)',
                borderColor: 'var(--idigest-chat-muted, #b0b0b0)'
              }
            }
          },
          checkmark: {
            color: 'var(--idigest-chat-fg, #000000) !important',
            fontWeight: '800 !important'
          }
        }
      })
    );
  }

  render() {
    const { position, currentMessage, nextMessage, previousMessage, user } = this.props;
    const sameUser = isSameUser(currentMessage, nextMessage);
    const sameUserAsPrevious = position === 'right' ? isSameUser(currentMessage, previousMessage) : false;
    const nextMessageIsRight = nextMessage?.user?.id === user.id;
    const nextMessageIsLeft = nextMessage?.user?.id !== user.id;

    let leftMessageSpacing;
    if (position === 'left' && nextMessageIsRight) {
      leftMessageSpacing = 12;
    } else if (position === 'right' && nextMessageIsLeft) {
      leftMessageSpacing = 14;
    } else {
      const isConsecutiveRightMessage = position === 'right' && (sameUserAsPrevious || sameUser);
      leftMessageSpacing = position === 'left' ? (sameUser ? 4 : 6) : isConsecutiveRightMessage ? 2 : 6;
    }
    const { isBatchSelectMode, containerStyle } = this.props;
    return (
      <View>
        {this.renderDay()}
        {currentMessage.system ? (
          this.renderSystemMessage()
        ) : (
          <View
            dataSet={{
              messageId: String(currentMessage?.id || ''),
              msgId: String(currentMessage?.id || '')
            }}
            style={[
              styles[position].container,
              { marginBottom: leftMessageSpacing },
              containerStyle[position],
              isBatchSelectMode && {
                position: 'relative',
                paddingRight: position === 'right' ? '29px' : '25px'
              }
            ]}
          >
            {position === 'left' ? this.renderAvatar() : null}
            {position === 'left' ? (
              <View style={styles.left.bubbleContainer}>
                {this.renderUsername()}
                {this.renderBubble()}
              </View>
            ) : (
              this.renderBubble()
            )}
            {position === 'right' ? this.renderAvatar() : null}
            {isBatchSelectMode && this.renderCheckbox()}
          </View>
        )}
      </View>
    );
  }
}

Message.defaultProps = {
  renderAvatar: undefined,
  renderBubble: null,
  renderDay: null,
  renderSystemMessage: null,
  position: 'left',
  currentMessage: {},
  nextMessage: {},
  previousMessage: {},
  user: {},
  containerStyle: {},
  showUserAvatar: true,
  inverted: true,
  isBatchSelectMode: false,
  selectedMessageIds: null,
  toggleMessageSelection: null
};

Message.propTypes = {
  renderAvatar: PropTypes.func,
  showUserAvatar: PropTypes.bool,
  renderBubble: PropTypes.func,
  renderDay: PropTypes.func,
  renderSystemMessage: PropTypes.func,
  position: PropTypes.oneOf(['left', 'right']),
  currentMessage: PropTypes.object,
  nextMessage: PropTypes.object,
  previousMessage: PropTypes.object,
  user: PropTypes.object,
  inverted: PropTypes.bool,
  isBatchSelectMode: PropTypes.bool,
  selectedMessageIds: PropTypes.object,
  toggleMessageSelection: PropTypes.func
  /*
  containerStyle: PropTypes.shape({
    left: ViewPropTypes.style,
    right: ViewPropTypes.style,
  }),
  */
};
